import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject, take, takeUntil } from 'rxjs';
import { ThreeGraphqlService } from 'src/app/core/graphql/three/services/three-graphql.service';
import {
  PartModel,
  ThreedProject,
  ThreedProjectPreset,
} from '../../services/project-controller/project-controller';
import { PresetDialogComponent } from './preset-dialog/preset-dialog.component';
import { CanvasControllerService } from 'src/app/core/services/canvas-controller/canvas-controller.service';
import { PartListControllerService } from '../../services/part-list-controller/part-list-controller.service';

@Component({
  selector: 'preset-tool',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './preset-tool.component.html',
  styleUrl: './preset-tool.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresetToolComponent implements OnInit, OnDestroy {
  public readonly project: InputSignal<ThreedProject | undefined> = input();
  public presets: ThreedProjectPreset[] = [];

  public embedProjects: any[] = [];

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private threeGraphqlService: ThreeGraphqlService,
    private canvasController: CanvasControllerService,
    private partListController: PartListControllerService,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.getPresets();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this._unsubscribeAll.unsubscribe();
  }

  public onClickPreset(preset: any): void {
    this.canvasController.canvas?.initObject();
    this.canvasController.clearAllStickers();

    const settings = JSON.parse(preset.setting);
    const stickers = preset.stickers.map((sticker: any) => JSON.parse(sticker));
    settings.forEach((setting: any) => {
      if (setting.style.type == 'color')
        this.canvasController.materialsColorChange([setting.material], setting.style.value);
    });
    this.partListController.categoryMap.forEach((partModel: PartModel) => {
      this.canvasController.removeModel(partModel);
      this.partListController.removePart(partModel);
      partModel.isSelected = false;
    });

    stickers.forEach((stickerData: any) => {
      console.log(stickerData);
      if (stickerData.isDualSided)
        this.canvasController.canvas?.attachDualSideDecal(
          stickerData.position,
          stickerData.rotation,
          stickerData.scale,
          stickerData.scaleRatio,
          stickerData.attachMeshNames,
          stickerData.stickerUrl,
          stickerData.renderOrder,
        );
      else
        this.canvasController.canvas?.attachOneSideDecal(
          stickerData.position,
          stickerData.rotation,
          stickerData.scale,
          stickerData.scaleRatio,
          stickerData.attachMeshNames,
          stickerData.stickerUrl,
          stickerData.renderOrder,
        );
    });

    preset.partModelUuids.forEach((uuid: string) => {
      const model = this.project()
        ?.partCategories?.map((category) => category.partModels)
        .flat()
        .find((model) => {
          return model.uuid === uuid;
        });
      if (!model) {
        this.threeGraphqlService
          .getPartModel(uuid)
          .pipe(take(1))
          .subscribe((model: PartModel | undefined) => {
            if (!model) return;
            this.canvasController.addModel(model).then(() => {
              model.isSelected = true;
              this.canvasController.stateManager.addModel(model);
              this.partListController.addPart(model);
              this.cd.markForCheck();
            });
          });
        return;
      }
      this.canvasController.addModel(model).then(() => {
        model.isSelected = true;
        this.canvasController.stateManager.addModel(model);
        this.partListController.addPart(model);
        this.cd.markForCheck();
      });
    });
    // this.toolsService.selectedPartModels.forEach((selectedModel: SelectedPartModelData) => {
    //   this.canvas()?.removeObject(selectedModel.object);
    //   let length =
    //     this.toolsService.defaultCategoryMap.get(selectedModel.partModel.category)?.length || 0;
    //   this.toolsService.defaultCategoryMap
    //     .get(selectedModel.partModel.category)
    //     ?.forEach((mesh: any, i: number) => {
    //       this.canvas()?.addObject(mesh);
    //       if (i == length - 1) {
    //         this.toolsService.removeSelectedPartModel(selectedModel.partModel.category);
    //       }
    //     });
    // });

    // setting.forEach((data: any) => {
    //   if (data.style.type == 'color')
    //     this.canvas()?.materialsColorChange(data.material, data.style.color);
    //   if (data.style.type == 'image')
    //     this.canvas()?.materialsMapChange(data.material, data.style.image);

    //   this.toolsService.addMaterialStyleData(data.material, data.style);
    // });

    // partsModelUuids.forEach((uuid: string) => {
    //   let index = 0;
    //   const model = this.project()
    //     ?.partCategories?.map((category) => category.partModels)
    //     .flat()
    //     .find((model, i) => {
    //       index = i;
    //       return model.uuid === uuid;
    //     });
    //   if (!model) return;
    //   const beforeModel: SelectedPartModelData[] = this.toolsService.selectedPartModels.filter(
    //     (selectedModel: SelectedPartModelData) =>
    //       selectedModel.partModel.category === model.category,
    //   );
    //   if (!model.category) return;
    //   if (beforeModel.length > 0) {
    //     this.canvas()?.removeObject(beforeModel[0].object);
    //     this.toolsService.removeSelectedPartModel(model.category);
    //   }

    //   if (index == 0) {
    //     this.toolsService.defaultCategoryMap.get(model.category)?.forEach((mesh: any) => {
    //       this.canvas()?.addObject(mesh);
    //     });
    //   } else {
    //     this.toolsService.defaultCategoryMap.get(model.category)?.forEach((mesh: any) => {
    //       this.canvas()?.removeObject(mesh);
    //     });
    //   }

    //   this.canvas()
    //     ?.addObfuscatedFile(model._d, model._f, model._o, model._u)
    //     .then((object: any) => {
    //       if (!model.category) return;
    //       this.toolsService.addSelectedPartModel({ partModel: model, object: object });
    //       this.cd.markForCheck();
    //     });
    // });

    // stickers.forEach((stickerData: any) => {
    //   this.canvas()?.attachStickerForJSON(stickerData);
    // });
  }

  public onCreateClick(): void {
    const imgString = this.canvasController.canvas?.screanShot();
    const setting = this.canvasController.stateManager.getSettings();
    const partModels = this.canvasController.stateManager
      .getPartModels()
      .map((model) => model.uuid);
    const stickers = this.canvasController.stateManager.getStickers().map((sticker) => {
      return {
        setting: sticker,
      };
    });
    const data = {
      setting,
      partModels,
      stickers,
    };
    const dialogRef = this.dialog.open(PresetDialogComponent, {
      data: {
        uuid: this.project()?.uuid,
        thumbnail: imgString,
        data: data,
      },
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((preset: ThreedProjectPreset) => {
        if (!preset) return;
        this.presets.push(preset);
        this.cd.markForCheck();
      });
  }

  public deletePreset(index: number): void {
    if (!this.presets[index]?.uuid) return;
    this.threeGraphqlService.deletePreset(this.presets[index].uuid).subscribe((res: any) => {
      if (!res.success) return;
      this.presets.splice(index, 1);
      this.cd.markForCheck();
    });
  }

  private getPresets(): void {
    this.threeGraphqlService
      .getProjectPresets(this.project()?.uuid)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((presets: ThreedProjectPreset[]) => {
        this.presets = presets || [];
        this.cd.markForCheck();
      });
  }
}
